import { useEffect, useRef, useState } from 'react';
import '../Dashboard.scss';
import * as echarts from 'echarts';
import IconButton from '@mui/material/IconButton';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { COMPACT_FONT_SIZE, downloadCSV, downloadIcon } from './utils';

const processCumulativeData = (data: void | any[], type = 'equip') => {
  if (!data) return;

  const listToRemove =
    type === 'equip'
      ? ['VRU', 'Dehydrator']
      : ['Conv_Well', 'Interconnect', 'Dehydration Facility'];
  const updated = data
    .map((item: any) => {
      if (item.type === 'Flare') {
        item.type = 'Flare / Combustor';
      }

      if (!listToRemove.includes(item.type)) {
        return item;
      }
    })
    .filter((item) => item);

  return updated;
};

const ChartComponent = ({
  type,
  results,
  title,
  compact = false,
}: {
  type?: string;
  results: any;
  title: string;
  compact?: boolean;
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!chartRef.current) return;

    chartInstance.current = echarts.init(chartRef.current);

    return () => {
      chartInstance.current?.dispose();
    };
  }, []);

  const updateChartOptions = () => {
    if (!chartInstance.current || !results || results.length === 0) return;

    let scatterData: { type: string; data: { x: number; y: number }[] }[] = [];

    const updatedResults = processCumulativeData(results, type);
    if (updatedResults) {
      updatedResults.forEach((result: { emissions: any[]; type: string }) => {
        const type = result.type;
        const data = result.emissions.map((emission) => ({
          x:
            emission['Cumulative Fraction'] || emission['cumulative_emissions'],
          y: emission['Cumulative Fraction ER'] || emission['cum_frac_ER'],
        }));

        scatterData.push({ type, data });
      });

      const option = {
        title: {
          text: title,
          left: 'center',
          ...(compact
            ? {
                textStyle: {
                  fontSize: COMPACT_FONT_SIZE.toString() + 'px',
                },
              }
            : {}),
        },
        grid: {
          top: compact ? 40 : 40,
          bottom: compact ? 80 : 50,
          left: compact ? 50 : 30,
          right: compact ? 20 : 30,
        },
        tooltip: {
          trigger: 'item',
          formatter: (params: any) => {
            return `Type: ${params.seriesName}<br/>Cumulative Fraction: ${params.data[0].toFixed(1)}<br/>Cumulative Fraction ER: ${params.data[1].toFixed(1)}`;
          },
        },
        xAxis: {
          type: 'value',
          name:
            'Cumulative Fraction of Emitter (%)' ||
            'Cumulative Fraction of Emitting Facility (%)',
          nameLocation: 'middle',
          nameGap: 70,
        },
        yAxis: {
          type: 'value',
          name: 'Cumulative Fraction of Emission (%)',
          nameLocation: 'middle',
          nameGap: 30,
        },
        series: scatterData.map((item) => ({
          name: item.type,
          type: 'scatter',
          data: item.data.map((point) => [point.x, point.y]),
          symbolSize: 5,
        })),
        legend: {
          data: scatterData.map((item) => item.type),
          top: 'bottom',
          padding: [
            0, // up
            0, // right
            10, // down
            0, // left
          ],
        },
        toolbox: {
          show: isFullscreen,
          right: '2%',
          feature: {
            saveAsImage: {},
            myDownload: {
              show: true,
              title: 'Download data',
              icon: downloadIcon,
              onclick: function () {
                downloadCSV(scatterData);
              },
            },
            dataZoom: [
              {
                type: 'slider',
              },
              {
                type: 'inside',
              },
            ],
          },
        },
        animation: false,
      };

      chartInstance.current.setOption(option);
    }
  };

  useEffect(() => {
    updateChartOptions();

    if (chartInstance.current) {
      chartInstance.current.resize();
    }
  }, [results, isFullscreen, title, compact]);

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  return (
    <div className={`chart-container ${isFullscreen ? 'fullscreen' : ''}`}>
      <IconButton
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '0px',
          left: '10px',
          zIndex: 1000,
          display: results.length > 0 ? 'block' : 'none',
        }}
      >
        {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
      </IconButton>
      <div ref={chartRef} className={'chart pie'} />
    </div>
  );
};

export default ChartComponent;
